import { Plus, Shield, Trash2, TriangleAlert } from 'lucide-react';
import { useId } from 'react';
import type { Diagnostic, NetworkKinds } from '../../api/types';
import { IconButton } from '../../components/ui';
import { strings } from '../../strings';
import { NetworkDiagnostic } from '../lists/parts';
import { providerLabel } from '../shared';
import type { FirewallForm } from './helpers';

const t = strings.networks.topology;

const tierLabel = (tier: string) => tier.charAt(0).toUpperCase() + tier.slice(1);

/**
 * The Settings tab's Firewall section (spec 11 6.15.12): per provider a switch (off by default), the tier with its price
 * per region and month, the egress choice and the allowed host names; diagnostics at their rows.
 */
export function FirewallSection({
  entries,
  value,
  readOnly,
  turnedOnByChoice,
  microSegmentation,
  diagnosticsAt,
  onChange,
}: {
  entries: NetworkKinds['firewalls'];
  /** Provider → the form's firewall (null: off and never set). */
  value: Record<string, FirewallForm | null>;
  readOnly: boolean;
  /** Providers whose firewall the "Through the hub firewall" choice turned on. */
  turnedOnByChoice: readonly string[];
  /** The form's topology is micro-segmentation (egress isn't filtered without the firewall's egress). */
  microSegmentation: boolean;
  diagnosticsAt: (path: string) => Diagnostic[];
  onChange: (provider: string, firewall: FirewallForm | null) => void;
}) {
  const ids = useId();
  if (!entries.length) return <p className="help">{t.noFirewallKind}</p>;
  return (
    <div className="space-y-4">
      {entries.map((entry) => {
        const p = entry.provider;
        const fw = value[p] ?? null;
        const on = !!fw?.enabled;
        const base = `/settings/providers/${p}/firewall`;
        const switchDiagnostics = [
          ...diagnosticsAt(`${base}/enabled`),
          ...diagnosticsAt('/settings/topology/spokeToSpoke'),
        ].filter((d) => d.code !== 'TOPOLOGY_CHOICE_REQUIRED');
        const sectionDiagnostics = diagnosticsAt(base).filter(
          (d) => d.path === base && d.code !== 'EGRESS_NOT_FILTERED',
        );
        const egressNotFiltered = microSegmentation && !(on && fw?.egress === 'through-firewall');
        const cost = (tier: string) => entry.tierCosts?.[tier];
        const tier = fw?.tier ?? entry.tiers.find((x) => x === 'standard') ?? entry.tiers[0];
        const asOf = Object.values(entry.tierCosts ?? {})[0]?.asOf;
        const hosts = fw?.allowedEgress ?? [];
        const update = (next: Partial<FirewallForm>) =>
          onChange(p, {
            enabled: on,
            tier,
            egress: fw?.egress ?? 'direct',
            allowedEgress: hosts,
            ...next,
          });
        return (
          <fieldset key={p} className="net-provider-block" data-testid={`firewall-${p}`}>
            <legend className="sr-only">{t.firewallSwitch(providerLabel(p))}</legend>
            <label className="net-switch-label flex items-center gap-2 font-semibold">
              <input
                type="checkbox"
                role="switch"
                checked={on}
                disabled={readOnly}
                aria-describedby={`${ids}-${p}-switch-help`}
                onChange={(e) =>
                  e.target.checked
                    ? update({ enabled: true })
                    : onChange(p, fw ? { ...fw, enabled: false } : null)
                }
              />
              <Shield aria-hidden className="size-4 text-fg-muted" />
              {t.firewallSwitch(providerLabel(p))}
            </label>
            <p id={`${ids}-${p}-switch-help`} className="help mt-1">
              {on
                ? turnedOnByChoice.includes(p)
                  ? t.firewallTurnedOnBy
                  : cost(tier)
                    ? t.tierOption(
                        tierLabel(tier),
                        `${cost(tier)!.currency} ${cost(tier)!.monthly.toLocaleString('en-US')}`,
                      )
                    : tierLabel(tier)
                : t.firewallOffHelp}
            </p>
            {switchDiagnostics.map((d, i) => (
              <p key={i} className="mt-1">
                <NetworkDiagnostic diagnostic={d} />
              </p>
            ))}
            {on && (
              <div className="mt-3 grid gap-x-4 gap-y-3 sm:grid-cols-2">
                <div>
                  <label htmlFor={`${ids}-${p}-tier`} className="label">
                    {t.tier}
                  </label>
                  <select
                    id={`${ids}-${p}-tier`}
                    className="input"
                    value={tier}
                    disabled={readOnly}
                    aria-describedby={`${ids}-${p}-tier-help`}
                    onChange={(e) => update({ tier: e.target.value })}
                  >
                    {entry.tiers.map((x) => {
                      const c = cost(x);
                      return (
                        <option key={x} value={x}>
                          {c
                            ? t.tierOption(tierLabel(x), `${c.currency} ${c.monthly.toLocaleString('en-US')}`)
                            : tierLabel(x)}
                        </option>
                      );
                    })}
                  </select>
                  {asOf && (
                    <p id={`${ids}-${p}-tier-help`} className="help">
                      {t.listPrice(asOf)}
                    </p>
                  )}
                </div>
                <fieldset>
                  <legend className="label">{t.egress}</legend>
                  {(
                    [
                      ['direct', t.egressDirect, t.egressDirectHelp],
                      ['through-firewall', t.egressFirewall, t.egressFirewallHelp],
                    ] as const
                  ).map(([v, label, help]) => (
                    <label key={v} className="mb-1 flex items-start gap-2 text-sm">
                      <input
                        type="radio"
                        name={`${ids}-${p}-egress`}
                        className="mt-1"
                        checked={(fw?.egress ?? 'direct') === v}
                        disabled={readOnly}
                        onChange={() => update({ egress: v })}
                      />
                      <span>
                        <span className="font-medium">{label}</span>
                        <span className="block text-[12.5px] text-fg-muted">{help}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
                <fieldset className="sm:col-span-2">
                  <legend className="label">{t.allowedEgress}</legend>
                  <p className="help mb-2">{t.allowedEgressHelp}</p>
                  <ul className="space-y-2">
                    {hosts.map((host, i) => {
                      const rowDiagnostics = diagnosticsAt(`${base}/allowedEgress/${i}`);
                      return (
                        <li key={i}>
                          <div className="flex items-center gap-2">
                            <input
                              className="input max-w-sm font-mono"
                              aria-label={t.hostLabel(i + 1)}
                              value={host}
                              readOnly={readOnly}
                              aria-invalid={rowDiagnostics.some((d) => d.severity === 'error') || undefined}
                              onChange={(e) =>
                                update({ allowedEgress: hosts.map((h, j) => (j === i ? e.target.value : h)) })
                              }
                            />
                            {!readOnly && (
                              <IconButton
                                label={t.removeHost(host)}
                                onClick={() => update({ allowedEgress: hosts.filter((_, j) => j !== i) })}
                              >
                                <Trash2 aria-hidden className="size-4" />
                              </IconButton>
                            )}
                          </div>
                          {rowDiagnostics.map((d, k) => (
                            <p key={k} className="mt-1">
                              <NetworkDiagnostic diagnostic={d} />
                            </p>
                          ))}
                        </li>
                      );
                    })}
                  </ul>
                  {!readOnly && (
                    <button
                      type="button"
                      className="btn btn-sm mt-2"
                      onClick={() => update({ allowedEgress: [...hosts, ''] })}
                    >
                      <Plus aria-hidden className="size-4" />
                      {t.addHost}
                    </button>
                  )}
                </fieldset>
              </div>
            )}
            {sectionDiagnostics.map((d, i) => (
              <p key={i} className="mt-2">
                <NetworkDiagnostic diagnostic={d} />
              </p>
            ))}
            {egressNotFiltered && (
              <p className="callout callout-warning mt-3" data-testid="egress-not-filtered">
                <TriangleAlert aria-hidden className="size-4 shrink-0" />
                <span>
                  {strings.networks.requests.egressNotFiltered}{' '}
                  <code className="font-mono text-xs">EGRESS_NOT_FILTERED</code>
                </span>
              </p>
            )}
          </fieldset>
        );
      })}
    </div>
  );
}
